import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ExternalLink, ListTodo, MonitorDown, Square } from "lucide-react";
import {
  GROOMING_QUEUE_CONCURRENCY,
  type GroomingQueueActionResult,
  type GroomingQueueEntry,
  type GroomingQueueState,
} from "@ttc-spectree/toolbox-contract";
import { QueueEntryRow, type QueueRowActions } from "@/components/grooming/queue/queue-entry-row";
import { QueuePauseBanner } from "@/components/grooming/queue/queue-pause-banner";
import { QueuePickedUpLine } from "@/components/grooming/queue/queue-picked-up-line";
import { QueueUsageLine } from "@/components/grooming/queue/queue-usage-line";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { usePageTitle } from "@/hooks/use-page-title";
import { localToday } from "@/lib/grooming/agenda-run";
import { groomingErrorMessage } from "@/lib/grooming/error-messages";
import { QUEUE_HIGHLIGHT_PARAM } from "@/lib/grooming/next-in-line";
import { TOOLBOX_SETUP_GUIDE_URL, useToolboxBridge } from "@/lib/toolbox/bridge";
import { runFixIt, useResumeAfterFixReturn, type FixIt, type FixItResult } from "@/lib/toolbox/fix-it";
import type { GroomingQueueActions } from "@/lib/toolbox/queue";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Queue screen (epic PI-SPEC-SW-E284, feature PI-SPEC-SW-1439), at
 * `/grooming/queue`.
 *
 * SpecTree Toolbox keeps one grooming run queue per computer and runs up to
 * "Run at once" entries on the person's own Claude Code seat. This page only
 * renders what the app reports (`useGroomingQueue`: no SpecTree API call)
 * and sends the actions back through the bridge: Running (phase, activity,
 * elapsed, Stop), Up next (place, Move up, Move down, Remove; followers
 * under their leader) and Finished today (entries that finished on this
 * computer's calendar day: result cards for briefs and author questions
 * with size, confidence, state and Open brief (E289, UX-PM-08); not ready,
 * failed in red with Retry, stopped with Retry, finished product-areas
 * drafts; Clear finished), plus
 * Stop all, the pause banner, Claude usage and the concurrency setting.
 *
 * Since epic PI-SPEC-SW-E290 (feature PI-SPEC-SW-1479, frames B1-B3) the
 * header says "Claude's runs on this computer · 3 of 11 done", a strip says
 * "Keeping this computer awake until the queue finishes." while the app
 * holds the computer awake and the usage in plain words, each pause has one
 * plain banner (a setup pause with one Fix it and Copy details for the
 * Admin), a picked-up line follows a wake or a fix, and Up next has Next in
 * line and Move to top. `?highlight=<entryId>` (feature PI-SPEC-SW-1482,
 * frame B4's "See it in the Queue") rings that row for a few seconds and
 * scrolls it into view.
 *
 * The queue is the person's own, so every control needs only a grooming
 * role (the route's RequireGroomingVisible); adding to the queue is gated
 * where the start buttons live. In a browser, and with an app older than
 * grooming contract 3, the page explains instead and shows no controls.
 */

/** The concurrency values the select offers, from the contract's one constant. */
export const CONCURRENCY_OPTIONS: readonly number[] = Array.from(
  { length: GROOMING_QUEUE_CONCURRENCY.max - GROOMING_QUEUE_CONCURRENCY.min + 1 },
  (_, offset) => GROOMING_QUEUE_CONCURRENCY.min + offset
);

/** The header's subtitle (frame B1). */
export const QUEUE_SUBTITLE = "Claude's runs on this computer";

/**
 * The header's subtitle: "Claude's runs on this computer · 3 of 11 done"
 * from `state.progress` (left out when the app does not send it), plus
 * " · paused" while paused. Pure; exported for tests.
 */
export function queueSubtitle(state: GroomingQueueState): string {
  const parts = [QUEUE_SUBTITLE];
  if (state.progress) parts.push(`${String(state.progress.done)} of ${String(state.progress.total)} done`);
  if (state.status === "paused") parts.push("paused");
  return parts.join(" · ");
}

/**
 * The note beside the Up next heading: "Starts as soon as a run finishes"
 * while runs are going, "Waiting for the fix" on a setup pause, else nothing.
 * Pure; exported for tests.
 */
export function upNextNote(state: GroomingQueueState): string | null {
  if (state.upNext.length === 0) return null;
  if (state.status === "paused") return state.pause?.reason === "setup" ? "Waiting for the fix" : null;
  return state.running.length > 0 ? "Starts as soon as a run finishes" : null;
}

/**
 * The finished entries that finished today in the browser's time zone, in
 * the app's order. An entry without a readable finish time stays: there is
 * nothing to say it is older. Pure; exported for tests.
 */
export function finishedToday(completed: readonly GroomingQueueEntry[], now: Date = new Date()): GroomingQueueEntry[] {
  const today = localToday(now);
  return completed.filter((entry) => {
    const at = entry.finishedAt ? new Date(entry.finishedAt) : null;
    return !at || Number.isNaN(at.getTime()) || localToday(at) === today;
  });
}

/** One Up next row with its place in Up next, and the entries that follow it in the same session. */
interface UpNextGroup {
  entry: GroomingQueueEntry;
  index: number;
  followers: { entry: GroomingQueueEntry; index: number }[];
}

/**
 * Group Up next for display: an entry whose `followerOf` names another Up
 * next entry is shown under that leader ("Also in session"); every other
 * entry keeps its own row in run order. Indexes stay the entries' places in
 * Up next, which Move up/down send to the app. Pure; exported for tests.
 */
export function groupUpNext(upNext: readonly GroomingQueueEntry[]): UpNextGroup[] {
  const ids = new Set(upNext.map((entry) => entry.id));
  const groups: UpNextGroup[] = [];
  const byLeader = new Map<string, UpNextGroup["followers"]>();
  upNext.forEach((entry, index) => {
    if (entry.followerOf && entry.followerOf !== entry.id && ids.has(entry.followerOf)) {
      const list = byLeader.get(entry.followerOf) ?? [];
      list.push({ entry, index });
      byLeader.set(entry.followerOf, list);
      return;
    }
    groups.push({ entry, index, followers: [] });
  });
  for (const group of groups) group.followers = byLeader.get(group.entry.id) ?? [];
  return groups;
}

/**
 * The project a repository sign-in from Fix it is for. The pause names no
 * project, so it is read from the queue: the app puts the run that hit the
 * sign-in back at the front of Up next, but while paused a single item added
 * next in line can go ahead of it. So the first waiting entry queued before
 * the pause began (`pause.since`) wins; without a readable `since` (an older
 * app), the first waiting entry; then the first running one. Pure; exported
 * for tests.
 */
export function repoSignInProject(state: Pick<GroomingQueueState, "pause" | "upNext" | "running">): string | null {
  const since = state.pause?.since ? Date.parse(state.pause.since) : Number.NaN;
  const before = Number.isNaN(since)
    ? undefined
    : state.upNext.find((entry) => {
        const queued = Date.parse(entry.queuedAt);
        return !Number.isNaN(queued) && queued <= since;
      });
  return before?.project ?? state.upNext[0]?.project ?? state.running[0]?.project ?? null;
}

/**
 * True when the picked-up line belongs on the page: the queue is not paused
 * and still has work. The app clears `pickedUp` once the next brief settles,
 * but not when nothing is left or only a product-areas draft ran, so the line
 * never says "0 briefs still to do" or lingers over an empty queue. Pure;
 * exported for tests.
 */
export function showsPickedUp(state: Pick<GroomingQueueState, "status" | "pickedUp" | "running" | "upNext">): boolean {
  return Boolean(state.pickedUp) && state.status !== "paused" && state.running.length + state.upNext.length > 0;
}

/** Plain words for a refused action: the engine's plain cause, or the app's message when the code says nothing more. */
function failureText(result: Extract<GroomingQueueActionResult, { ok: false }>): string {
  return result.errorCode === "UNKNOWN" ? result.message : groomingErrorMessage(result.errorCode).cause;
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="glass-card flex max-w-xl items-start gap-3 rounded-[10px] px-5 py-[18px]" data-testid="queue-unavailable">
      <MonitorDown className="mt-0.5 h-4 w-4 flex-none text-primary" aria-hidden="true" />
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}

/** Why there is no queue here: a browser, or an app older than grooming contract 3. */
function QueueUnavailable({ inToolbox }: { inToolbox: boolean }) {
  if (!inToolbox) {
    return (
      <Card>
        <p>The queue runs in SpecTree Toolbox on your computer. Open SpecTree Toolbox to see it.</p>
        <a
          href={TOOLBOX_SETUP_GUIDE_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-link hover:underline"
        >
          How to install it
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      </Card>
    );
  }
  return (
    <Card>
      <p>Update SpecTree Toolbox to line up grooming runs.</p>
    </Card>
  );
}

function Section({
  title,
  count,
  action,
  empty,
  children,
  testId,
}: {
  title: string;
  count: number;
  action?: ReactNode;
  empty: string;
  children: ReactNode;
  testId: string;
}) {
  const headingId = `${testId}-heading`;
  return (
    <section aria-labelledby={headingId} className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <h2 id={headingId} className="font-display text-sm font-semibold">
          {title}
        </h2>
        <span className="font-mono text-xs text-muted-foreground">{count}</span>
        <span className="flex-1" />
        {action}
      </div>
      {count === 0 ? <p className={cn("text-sm", TOOLBOX_TEXT.faint)}>{empty}</p> : <ol className="space-y-1.5">{children}</ol>}
    </section>
  );
}

/** The concurrency select: "Run at once: 1 2 3 4". */
function ConcurrencySelect({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled: boolean;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <div className="flex items-center gap-2">
        <span id="queue-concurrency-label" className="text-xs text-muted-foreground">
          Run at once
        </span>
        <Select
          value={String(value)}
          disabled={disabled}
          onValueChange={(next) => {
            const n = Number(next);
            if (Number.isInteger(n) && n !== value) onChange(n);
          }}
        >
          <SelectTrigger
            className="h-8 w-[64px] rounded-lg md:h-8"
            aria-labelledby="queue-concurrency-label"
            data-testid="queue-concurrency-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONCURRENCY_OPTIONS.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <span
        className={cn("text-xs", TOOLBOX_TEXT.faint)}
        title="Lowering it never stops runs that are already going; it applies as they finish."
        data-testid="queue-concurrency-help"
      >
        More runs finish sooner and use your Claude limit faster.
      </span>
    </div>
  );
}

/** The queue itself, once the app reported it. */
/** How long the row "See it in the Queue" opened stays ringed. */
export const QUEUE_HIGHLIGHT_MS = 3000;

/**
 * The entry `?highlight=<entryId>` names (E290, frame B4: "See it in the
 * Queue" from One item or the reading pane), for {@link QUEUE_HIGHLIGHT_MS};
 * null after that or without the parameter.
 */
function useHighlightedEntry(): string | null {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get(QUEUE_HIGHLIGHT_PARAM);
  const [highlighted, setHighlighted] = useState<string | null>(requested);
  useEffect(() => {
    setHighlighted(requested);
    if (!requested) return undefined;
    const timer = setTimeout(() => {
      setHighlighted(null);
    }, QUEUE_HIGHLIGHT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [requested]);
  return highlighted;
}

function QueueBoard({ state, actions }: { state: GroomingQueueState; actions: GroomingQueueActions }) {
  const highlighted = useHighlightedEntry();
  const bridge = useToolboxBridge();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  /** Run one action under a busy key; a refusal shows in plain words. */
  const run = useCallback((key: string, action: () => Promise<GroomingQueueActionResult>) => {
    setBusy((current) => new Set(current).add(key));
    setError(null);
    void action().then((result) => {
      setBusy((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
      if (!result.ok) setError(failureText(result));
    });
  }, []);

  // Back from a Fix it on Setup (?fixed=1): ask the app to start again once.
  const onFixReturn = useCallback((result: GroomingQueueActionResult) => {
    if (!result.ok) setError(failureText(result));
  }, []);
  const resumeQueue = useCallback(() => actions.resume(), [actions]);
  useResumeAfterFixReturn(state, resumeQueue, onFixReturn);

  const rowActions = useMemo<QueueRowActions>(
    () => ({
      stop: (id) => {
        run(id, () => actions.stop(id));
      },
      remove: (id) => {
        run(id, () => actions.remove(id));
      },
      move: (id, toIndex) => {
        run(id, () => actions.move(id, toIndex));
      },
      retry: (id) => {
        run(id, () => actions.retry(id));
      },
    }),
    [actions, run]
  );

  const resume = () => {
    run("resume", () => actions.resume());
  };
  const paused = state.status === "paused";
  const left = state.running.length + state.upNext.length;
  const fixProject = repoSignInProject(state);

  /** Fix it on a setup pause: the one repair, then a resume (01-roles.md section 9.6). */
  const onFixIt = useCallback(
    (fix: FixIt): Promise<FixItResult> => {
      if (!bridge) return Promise.resolve({ kind: "failed", message: "Open SpecTree Toolbox to fix this." });
      return runFixIt(fix, {
        bridge,
        resume: resumeQueue,
        navigate: (to) => {
          void navigate(to);
        },
        project: fixProject,
      });
    },
    [bridge, fixProject, navigate, resumeQueue]
  );
  const groups = groupUpNext(state.upNext);
  const upNext = upNextNote(state);
  const finished = finishedToday(state.completed);
  const canStopAll = state.running.length > 0 || (!paused && state.upNext.length > 0);

  return (
    <div className="space-y-5" data-testid="queue-board">
      <header className="flex flex-wrap items-start gap-3 border-b border-[#1F1F23] pb-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className={cn("flex items-center gap-2", TOOLBOX_PAGE_TITLE)}>
            <ListTodo className={cn("h-4 w-4", TOOLBOX_TEXT.faint)} aria-hidden="true" />
            Queue
          </h1>
          <p className="text-xs text-muted-foreground" data-testid="queue-status-line">
            {queueSubtitle(state)}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canStopAll || busy.has("stop-all")}
            onClick={() => {
              run("stop-all", () => actions.stopAll());
            }}
          >
            <Square className="mr-1.5 h-3 w-3" aria-hidden="true" />
            Stop all
          </Button>
          <ConcurrencySelect
            value={state.concurrency}
            disabled={busy.has("concurrency")}
            onChange={(n) => {
              run("concurrency", () => actions.setConcurrency(n));
            }}
          />
        </div>
      </header>

      <QueueUsageLine
        usage={state.usage}
        estimate={state.estimate}
        waiting={state.upNext.length}
        keepingAwake={state.keepingAwake}
        usageCheck={state.usageCheck}
        runsInFlight={state.running.length > 0}
        {...(actions.canRefreshUsage ? { onRefreshUsage: actions.refreshUsage } : {})}
      />

      {state.pause && (
        <QueuePauseBanner pause={state.pause} left={left} onResume={resume} busy={busy.has("resume")} onFixIt={onFixIt} />
      )}

      {state.pickedUp && showsPickedUp(state) && <QueuePickedUpLine pickedUp={state.pickedUp} left={left} />}

      {error && (
        <p className="text-sm text-[#F87171]" role="alert" data-testid="queue-error">
          {error}
        </p>
      )}

      <Section title="Running" count={state.running.length} empty="Nothing is running." testId="queue-running">
        {state.running.map((entry) => (
          <QueueEntryRow
            key={entry.id}
            entry={entry}
            section="running"
            actions={rowActions}
            busy={busy.has(entry.id)}
            highlighted={entry.id === highlighted}
          />
        ))}
      </Section>

      <Section
        title="Up next"
        count={state.upNext.length}
        empty="Nothing is waiting."
        testId="queue-up-next"
        action={
          upNext ? (
            <span className={cn("text-xs", TOOLBOX_TEXT.faint)} data-testid="queue-up-next-note">
              {upNext}
            </span>
          ) : undefined
        }
      >
        {groups.map((group) => (
          <QueueGroup
            key={group.entry.id}
            group={group}
            count={state.upNext.length}
            actions={rowActions}
            busy={busy}
            highlighted={highlighted}
          />
        ))}
      </Section>

      <Section
        title="Finished today"
        count={finished.length}
        empty="Nothing has finished yet."
        testId="queue-completed"
        action={
          finished.length > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy.has("clear")}
              onClick={() => {
                run("clear", () => actions.clearCompleted());
              }}
            >
              Clear finished
            </Button>
          ) : undefined
        }
      >
        {finished.map((entry) => (
          <QueueEntryRow key={entry.id} entry={entry} section="completed" actions={rowActions} busy={busy.has(entry.id)} />
        ))}
      </Section>
    </div>
  );
}

function QueueGroup({
  group,
  count,
  actions,
  busy,
  highlighted,
}: {
  group: UpNextGroup;
  count: number;
  actions: QueueRowActions;
  busy: ReadonlySet<string>;
  /** The entry "See it in the Queue" opened, or null. */
  highlighted: string | null;
}) {
  return (
    <>
      <QueueEntryRow
        entry={group.entry}
        section="upNext"
        index={group.index}
        count={count}
        actions={actions}
        busy={busy.has(group.entry.id)}
        highlighted={group.entry.id === highlighted}
      />
      {group.followers.map((follower) => (
        <QueueEntryRow
          key={follower.entry.id}
          entry={follower.entry}
          section="upNext"
          index={follower.index}
          count={count}
          follower
          actions={actions}
          busy={busy.has(follower.entry.id)}
          highlighted={follower.entry.id === highlighted}
        />
      ))}
    </>
  );
}

/** `/grooming/queue`. */
export function GroomingQueuePage() {
  usePageTitle("Queue");
  const inToolbox = useToolboxBridge() !== null;
  const { supported, state, actions } = useGroomingQueue();

  if (!supported) {
    return (
      <div className="space-y-4" data-testid="queue-page">
        <h1 className={TOOLBOX_PAGE_TITLE}>Queue</h1>
        <QueueUnavailable inToolbox={inToolbox} />
      </div>
    );
  }

  return (
    <div data-testid="queue-page">
      {state ? (
        <QueueBoard state={state} actions={actions} />
      ) : (
        <p className="text-sm text-muted-foreground" role="status">
          Reading the queue from SpecTree Toolbox…
        </p>
      )}
    </div>
  );
}
